import React from "react";
import { useFragment } from "react-relay";

import { type Chapter, ControlTooltip } from ".";
import { faPause, faPlay } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import {
	faDeleteLeft,
	faDeleteRight,
	faDesktop,
	faMessageLines,
	faUserMinus,
	faUserPlus,
} from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { graphql } from "relay-runtime";

import { AppContext } from "~/common";
import { ReactComponent as StopScreenshareIcon } from "~/legacy-assets/images/icons/stop-screenshare.svg";
import Tooltip from "~/ui/Tooltip";

import { getEventDescriptionByEventType, type KeyEvent } from "../TranscriptPanel";
import { useIsNarrowViewport } from "../utils";

import type {
	Timeline_meetingKeyEvents$data as KeyEventsData,
	Timeline_meetingKeyEvents$key,
} from "~/__generated__/Timeline_meetingKeyEvents.graphql";

import styles from "./Timeline.module.css";

interface BaseVideoProps {
	chapters: readonly Readonly<Chapter>[];
	keyEventsKey: (Timeline_meetingKeyEvents$key & { __typename: string }) | null;
}

function cssPercentage(dividend: number, divisor: number) {
	return `${Math.min((dividend / divisor) * 100, 100)}%`;
}

// This wrapper component exists only to defer rendering the timeline itself
// until the video metadata has been downloaded. This way we know the video
// duration immediately in TimelineContent.
export function Timeline({ videoRef, ...props }: { videoRef: React.RefObject<HTMLVideoElement> } & BaseVideoProps) {
	const [videoDuration, setVideoDuration] = React.useState<number | undefined>(undefined);

	React.useEffect(() => {
		function setDurationFromVideo() {
			const video = videoRef.current;
			if (!video) return;
			setVideoDuration(video.duration);
		}

		const video = videoRef.current;
		if (!video) {
			return;
		}

		setDurationFromVideo();

		video.addEventListener("loadedmetadata", setDurationFromVideo);
		return () => {
			video.removeEventListener("loadedmetadata", setDurationFromVideo);
		};
	}, [videoRef]);

	if (videoDuration === undefined) {
		return null;
	}

	return <TimelineContent {...props} videoRef={videoRef} videoDuration={videoDuration} />;
}

function getChapterTimestampFromPointerEvent(event: React.PointerEvent) {
	const target = event.target as HTMLDivElement;
	const chapter = target.closest(`.${styles.chapterWrapper}`) as HTMLDivElement;
	if (!chapter) {
		return;
	}
	return Number(chapter.dataset.chapterTimestamp);
}

function getTimestampFromPointerEvent(event: React.PointerEvent) {
	const target = event.target as HTMLDivElement;
	const chapter = target.closest(`.${styles.chapterWrapper}`) as HTMLDivElement;
	if (!chapter) {
		return;
	}
	const pctg = event.nativeEvent.offsetX / chapter.offsetWidth;
	return target.dataset.keyEventTime
		? Number(target.dataset.keyEventTime)
		: Number(chapter.dataset.chapterTimestamp) + Number(chapter.dataset.chapterDuration) * pctg;
}

export interface ChapterWithDurationAndRef extends Chapter {
	duration: number;
	ref: React.RefObject<HTMLDivElement>;
	keyEvents: KeyEvent[] | undefined | null;
}

export interface ChapterWithTimestamp extends Chapter {
	timestamp: number;
}

function updateChapterWrapperFromHover(
	chapter: ChapterWithDurationAndRef & ChapterWithTimestamp,
	hoveringPosition: { timestamp: number } | undefined,
) {
	const chapterWrapper = chapter.ref.current;
	if (!chapterWrapper) return;

	if (!hoveringPosition) {
		chapterWrapper.classList.toggle(styles.hoveringAfter, false);
		chapterWrapper.style.setProperty("--hover-percentage", "0%");
	} else {
		chapterWrapper.classList.toggle(
			styles.hoveringAfter,
			hoveringPosition.timestamp > chapter.timestamp + chapter.duration,
		);
		chapterWrapper.style.setProperty(
			"--hover-percentage",
			cssPercentage(hoveringPosition.timestamp - chapter.timestamp, chapter.duration),
		);
	}
}

function isEquivalentToKeyEventTime(
	keyEventTime: number,
	currentTime: number,
	chapterWidth: number,
	chapterDuration: number,
	difference = 2, // pixels
) {
	// Determines the pixel difference between the current time and the key event time.
	// This difference is compared against the 'difference' threshold to determine whether the currentTime
	// can be approximated to a key event. This is necessary because a click may not be exactly on the key event.
	if (chapterWidth === 0 || chapterDuration === 0) return false;
	const scaleFactor = chapterWidth / chapterDuration; // How large things currently appear on the screen
	const currentPosition = currentTime * scaleFactor;
	const keyEventPosition = keyEventTime * scaleFactor;
	return Math.abs(currentPosition - keyEventPosition) < difference;
}

function updateChapterWrapperFromCurrentTime(
	chapter: ChapterWithDurationAndRef & ChapterWithTimestamp,
	currentTime: number,
) {
	const chapterWrapper = chapter.ref.current;
	if (!chapterWrapper) return;
	const chapterWidth = chapterWrapper.offsetWidth;
	const chapterDuration = chapter.duration;
	const sameTimeAsKeyEvent =
		chapter.keyEvents?.some(keyEvent =>
			isEquivalentToKeyEventTime(keyEvent.timeFromRecordingStart, currentTime, chapterWidth, chapterDuration),
		) ?? "false";

	chapterWrapper.dataset.isKeyEvent = sameTimeAsKeyEvent.toString();

	chapterWrapper.dataset.chapterActive = (
		currentTime >= chapter.timestamp && currentTime < chapter.timestamp + chapter.duration
	).toString();
	chapterWrapper.style.setProperty(
		"--progress-percentage",
		cssPercentage(currentTime - chapter.timestamp, chapter.duration),
	);
}

function updateChapterWrapperFromDownloadProgress(
	chapter: ChapterWithDurationAndRef & ChapterWithTimestamp,
	downloadProgress: number,
) {
	const chapterWrapper = chapter.ref.current;
	if (!chapterWrapper) return;

	chapterWrapper.style.setProperty(
		"--buffered-percentage",
		cssPercentage(downloadProgress - chapter.timestamp, chapter.duration),
	);
}

function coalesceNaN(value: number, fallback: number) {
	return isNaN(value) ? fallback : value;
}

const TOOLTIP_OFFSET: [number, number] = [0, 0];

const iconByEventType: { [key: string]: JSX.Element } = {
	join: <FontAwesomeIcon icon={faUserPlus} />,
	leave: <FontAwesomeIcon icon={faUserMinus} />,
	/* eslint-disable camelcase */
	screenshare_start: <FontAwesomeIcon icon={faDesktop} />,
	screenshare_stop: <StopScreenshareIcon />,
	pause_recording: <FontAwesomeIcon icon={faPause} />,
	resume_recording: <FontAwesomeIcon icon={faPlay} />,
	redaction: <FontAwesomeIcon icon={faDeleteRight} />,
	redaction_end: <FontAwesomeIcon icon={faDeleteLeft} />,
	/* eslint-enable camelcase */
};

// if the key event has a duration, it must have a corresponding end key event defined here.
const correspondingEndKeyEventType: { [key: string]: string } = {
	// eslint-disable-next-line camelcase
	pause_recording: "resume_recording",
	redaction: "redaction_end",
};

type CommentsGroup = { timestamp: number };

function getGroupedCommentAnnotations(
	comments: KeyEventsData["comments"],
	videoDuration: number,
): Array<CommentsGroup> {
	// Sort comments by timestamp
	const sortedComments = [...comments]
		.map(comment => ({
			...comment,
			startTimestamp: Number.parseFloat(comment.startTimestamp),
		}))
		.sort((a, b) => a.startTimestamp - b.startTimestamp);

	// Calculate minimum time difference based on timeline dimensions
	const TIMELINE_WIDTH_PX = 660; // pixels
	const COMMENT_ICON_WIDTH_PX = 10; // pixels
	const MIN_ICON_SPACING_PX = 5; // pixels of minimum space between icons

	// Calculate how many seconds each pixel represents
	const secondsPerPixel = videoDuration / TIMELINE_WIDTH_PX;

	// Calculate minimum time difference needed to avoid overlap
	const minTimeDifference = (COMMENT_ICON_WIDTH_PX + MIN_ICON_SPACING_PX) * secondsPerPixel;
	// The first comments should not overflow the timeline
	const minTimestamp = secondsPerPixel * 5;

	// Group comments that are close to each other
	const commentGroups: { timestamp: number }[] = [];

	for (const comment of sortedComments) {
		const timestamp = Math.max(comment.startTimestamp, minTimestamp);
		const lastGroup = commentGroups[commentGroups.length - 1];

		if (lastGroup && timestamp - lastGroup.timestamp < minTimeDifference) {
			// Skip this comment as it would overlap with the previous one
			continue;
		}

		// Create a new group (just storing the timestamp)
		commentGroups.push({ timestamp });
	}

	return commentGroups;
}

export const keyEventsFragment = graphql`
	fragment Timeline_meetingKeyEvents on MeetingRecording {
		meetingKeyEvents {
			eventType
			participantName
			timeFromRecordingStart
			endTimeFromRecordingStart
			duration
		}
		comments {
			threadId
			startTimestamp
		}
	}
`;

function TimelineContent({
	videoDuration,
	videoRef,
	...props
}: {
	videoDuration: number;
	videoRef: React.RefObject<HTMLVideoElement>;
} & BaseVideoProps) {
	const user = React.useContext(AppContext);

	const meetingRecording = useFragment(
		keyEventsFragment,
		props.keyEventsKey && props.keyEventsKey.__typename === "MeetingRecording" ? props.keyEventsKey : null,
	);
	const keyEvents = meetingRecording?.meetingKeyEvents;
	const comments = meetingRecording?.comments;

	// For events with a duration, we need to expand them into two separate events with the 'end event' having a duration of 0.
	// Currently this is only done for pause_recording events.
	const expandedKeyEvents: KeyEvent[] = React.useMemo(() => {
		const output: KeyEvent[] = [];

		for (const keyEvent of keyEvents || []) {
			/**
			 * Previously, we didn't record `endTimeFromRecordingStart` separately from `duration`,
			 * so old key events may not have it. In that case we assume that it's equal to
			 * `timeFromRecordingStart + duration`, if `duration` is present.
			 * */
			const effectiveEndTime =
				keyEvent.endTimeFromRecordingStart ??
				(keyEvent.duration != null ? keyEvent.timeFromRecordingStart + keyEvent.duration : null);

			if (effectiveEndTime != null && effectiveEndTime > keyEvent.timeFromRecordingStart) {
				output.push(
					{
						eventType: keyEvent.eventType,
						participantName: keyEvent.participantName,
						timeFromRecordingStart: keyEvent.timeFromRecordingStart,
						endTimeFromRecordingStart: null,
						duration: 0,
					},
					{
						eventType: correspondingEndKeyEventType[keyEvent.eventType],
						participantName: keyEvent.participantName,
						timeFromRecordingStart: effectiveEndTime,
						endTimeFromRecordingStart: null,
						duration: 0,
					},
				);
			} else {
				output.push({
					participantName: keyEvent.participantName,
					timeFromRecordingStart: keyEvent.timeFromRecordingStart,
					endTimeFromRecordingStart: keyEvent.endTimeFromRecordingStart,
					duration: keyEvent.duration,
					eventType: keyEvent.eventType,
				});
			}
		}

		// Group comments that are close to each other to avoid icon overlap
		if (videoDuration && comments && comments.length > 0) {
			const groups = getGroupedCommentAnnotations(comments, videoDuration);
			output.push(
				...groups.map(group => ({
					eventType: "comment",
					timeFromRecordingStart: group.timestamp,
					endTimeFromRecordingStart: null,
					participantName: null,
					duration: 0,
				})),
			);
		}

		return output;
	}, [keyEvents, comments, videoDuration]);

	const setVideoCurrentTime = React.useCallback(
		(time: number) => {
			const video = videoRef.current;
			if (!video) return;
			video.currentTime = time;
		},
		[videoRef],
	);

	const chapters = React.useMemo<readonly (ChapterWithDurationAndRef & ChapterWithTimestamp)[]>(() => {
		// Show all screenshare events and only join/leave events for the current user
		const keyEventsForUser = expandedKeyEvents?.filter(keyEvent =>
			keyEvent.eventType === "join" || keyEvent.eventType === "leave"
				? keyEvent.participantName === user?.user?.fullName
				: true,
		);

		let chaptersWithTimestamps: ChapterWithTimestamp[];
		chaptersWithTimestamps = props.chapters.filter(
			(chapter): chapter is ChapterWithTimestamp => chapter.timestamp != null,
		);

		if (chaptersWithTimestamps.length === 0) {
			return [
				{
					timestamp: 0,
					title: "",
					duration: coalesceNaN(videoDuration, 0),
					ref: React.createRef<HTMLDivElement>(),
					keyEvents: keyEventsForUser,
				},
			];
		}
		const chapters = [];

		for (let i = 0; i < chaptersWithTimestamps.length; i += 1) {
			const chapter = chaptersWithTimestamps[i];
			const nextChapter = chaptersWithTimestamps[i + 1];
			let { timestamp } = chapter;

			if (i === 0) {
				if (timestamp === null || timestamp < 1) {
					timestamp = 0;
				} else {
					chapters.push({
						timestamp: 0,
						title: "Introduction",
						duration: timestamp,
						ref: React.createRef<HTMLDivElement>(),
						keyEvents: keyEventsForUser?.filter(keyEvent => keyEvent.timeFromRecordingStart <= timestamp),
					});
				}
			}
			chapters.push({
				timestamp,
				title: chapter.title,
				duration: nextChapter ? nextChapter.timestamp - timestamp : coalesceNaN(videoDuration, timestamp) - timestamp,
				ref: React.createRef<HTMLDivElement>(),
				keyEvents: keyEventsForUser?.filter(
					keyEvent =>
						keyEvent.timeFromRecordingStart >= timestamp &&
						keyEvent.timeFromRecordingStart < (nextChapter?.timestamp ?? videoDuration),
				),
			});
		}
		return chapters;
	}, [props.chapters, user?.user?.fullName, videoDuration, expandedKeyEvents]);

	const [pointerIsDown, setPointerIsDown] = React.useState<{ pointerId: number } | undefined>();
	// This ref is so event listeners don't need to be recreated when
	// pointerIsDown changes.
	const pointerIsDownRef = React.useRef<{ pointerId: number } | undefined>(pointerIsDown);
	pointerIsDownRef.current = pointerIsDown;

	const onPointerDown = React.useCallback(
		(event: React.PointerEvent) => {
			if (
				(pointerIsDownRef.current && pointerIsDownRef.current.pointerId !== event.pointerId) ||
				(event.pointerType === "mouse" && event.button !== 0)
			) {
				setPointerIsDown(undefined);
				return;
			}
			event.preventDefault();
			setPointerIsDown({ pointerId: event.pointerId });
			const timestamp = getTimestampFromPointerEvent(event);
			if (timestamp) {
				setVideoCurrentTime(timestamp);
				const video = videoRef.current;
				if (!video) return;
				for (const chapter of chapters) {
					updateChapterWrapperFromCurrentTime(chapter, video.currentTime);
				}
			}
		},
		[chapters, setVideoCurrentTime, videoRef],
	);

	// Global event listeners for pointerup/down so if you click and hold in the
	// timeline, then move your pointer outside and release it or start dragging
	// with another pointer, we don't keep seeking the video.
	React.useEffect(() => {
		function onPointerUp() {
			setPointerIsDown(undefined);
		}

		function onPointerDown(event: PointerEvent) {
			if (pointerIsDownRef.current && pointerIsDownRef.current.pointerId !== event.pointerId) {
				setPointerIsDown(undefined);
				return;
			}
		}

		window.addEventListener("pointerup", onPointerUp);
		window.addEventListener("pointerdown", onPointerDown);

		return () => {
			window.removeEventListener("pointerup", onPointerUp);
			window.removeEventListener("pointerdown", onPointerDown);
		};
	}, []);

	const onPointerMove = React.useCallback(
		(event: React.PointerEvent) => {
			const timestamp = getTimestampFromPointerEvent(event);
			if (timestamp) {
				for (const chapter of chapters) {
					updateChapterWrapperFromHover(chapter, { timestamp });
				}
				if (pointerIsDownRef.current) {
					for (const chapter of chapters) {
						updateChapterWrapperFromCurrentTime(chapter, timestamp);
					}
					setVideoCurrentTime(timestamp);
				}
			}
		},
		[chapters, setVideoCurrentTime],
	);
	const onPointerOut = React.useCallback(() => {
		for (const chapter of chapters) {
			updateChapterWrapperFromHover(chapter, undefined);
		}
	}, [chapters]);

	React.useEffect(() => {
		const video = videoRef.current;
		if (!video) return;

		function onTimeUpdate() {
			const video = videoRef.current;
			if (!video || pointerIsDownRef.current) return;
			for (const chapter of chapters) {
				updateChapterWrapperFromCurrentTime(chapter, video.currentTime);
			}
		}

		function onProgress() {
			const video = videoRef.current;
			if (!video) return;

			let bufferedTime;
			if (video.buffered.length === 0) {
				bufferedTime = 0;
			} else {
				bufferedTime = video.buffered.end(video.buffered.length - 1);
			}
			for (const chapter of chapters) {
				updateChapterWrapperFromDownloadProgress(chapter, bufferedTime);
			}
		}

		onTimeUpdate();
		onProgress();

		video.addEventListener("timeupdate", onTimeUpdate);
		video.addEventListener("progress", onProgress);
		return () => {
			video.removeEventListener("timeupdate", onTimeUpdate);
			video.removeEventListener("progress", onProgress);
		};
	}, [chapters, videoDuration, videoRef]);

	const [chapterTooltipShownForTimestamp, setChapterTooltipShown] = React.useState<number>();
	const [keyEventTooltipShownForTimestamp, setKeyEventTooltipShown] = React.useState<number>();

	const hideChapterTooltip = React.useCallback(() => setChapterTooltipShown(undefined), []);

	const showKeyEventTooltip = React.useCallback(
		(event: React.PointerEvent<HTMLDivElement>) => {
			if (!(event.target instanceof HTMLElement)) return;
			hideChapterTooltip();
			const chapterTimestamp = event.target.dataset.chapterTimestamp
				? parseFloat(event.target.dataset.chapterTimestamp)
				: 0;
			const timestamp = getTimestampFromPointerEvent(event) ?? 0;
			setKeyEventTooltipShown(timestamp - chapterTimestamp);
		},
		[hideChapterTooltip],
	);

	const showChapterTooltip = React.useCallback(
		(event: React.PointerEvent<HTMLDivElement>) => {
			if (!(event.target instanceof HTMLElement)) return;
			if (event.target.dataset.isKeyEvent === "true") {
				showKeyEventTooltip(event);
			} else {
				const timestamp = getChapterTimestampFromPointerEvent(event);
				setChapterTooltipShown(timestamp);
			}
		},
		[showKeyEventTooltip],
	);

	const hideKeyEventTooltip = React.useCallback(
		(event: React.PointerEvent<HTMLDivElement>) => {
			showChapterTooltip(event);
			setKeyEventTooltipShown(undefined);
		},
		[showChapterTooltip],
	);

	const keyEventPointerDown = React.useCallback(
		(event: React.PointerEvent<HTMLDivElement>) => {
			hideChapterTooltip();
			onPointerDown(event);
		},
		[hideChapterTooltip, onPointerDown],
	);

	const narrowViewport = useIsNarrowViewport();

	return (
		<div className={styles.timelinesContainer}>
			<div
				className={styles.timeline}
				style={{ "--video-duration": videoDuration, "--video-chapter-count": chapters.length }}
				onPointerMove={onPointerMove}
				onPointerDown={onPointerDown}
				data-pointer-down={pointerIsDown ? "true" : "false"}
			>
				{chapters.map(chapter => (
					<ControlTooltip
						key={chapter.timestamp}
						content={chapter.title}
						disabled={!chapter.title}
						offset={TOOLTIP_OFFSET}
						visible={chapterTooltipShownForTimestamp === chapter.timestamp}
					>
						<div
							ref={chapter.ref}
							className={cx(styles.chapterWrapper)}
							style={{
								"--chapter-duration": chapter.duration,
							}}
							onPointerEnter={showChapterTooltip}
							onPointerLeave={hideChapterTooltip}
							onPointerOut={onPointerOut}
							data-chapter-timestamp={chapter.timestamp}
							data-chapter-duration={chapter.duration}
						>
							<div className={styles.chapter}>
								{!narrowViewport && (
									<div className={styles.keyEventsTimeline}>
										{chapter.keyEvents?.map(keyEvent => {
											const position = ((keyEvent.timeFromRecordingStart - chapter.timestamp) / chapter.duration) * 100;
											const participantName =
												keyEvent.participantName === user.user?.fullName ? "You" : keyEvent.participantName;
											return (
												<Tooltip
													key={`${keyEvent.timeFromRecordingStart}-${keyEvent.eventType}-${keyEvent.participantName}`}
													content={
														<div className={styles.keyEventTooltip}>
															{iconByEventType[keyEvent.eventType]}
															{participantName}{" "}
															{getEventDescriptionByEventType(
																keyEvent.eventType,
																participantName === "You" ? "your" : "their",
																keyEvent.duration,
															)}
														</div>
													}
													visible={Boolean(
														keyEventTooltipShownForTimestamp &&
															isEquivalentToKeyEventTime(
																keyEvent.timeFromRecordingStart,
																keyEventTooltipShownForTimestamp,
																chapter.ref.current?.offsetWidth ?? 0,
																chapter.duration,
															) &&
															keyEvent.eventType !== "comment",
													)}
												>
													<div
														onPointerEnter={showKeyEventTooltip}
														onPointerLeave={hideKeyEventTooltip}
														onPointerDown={keyEventPointerDown}
														className={cx(styles.keyEvent, {
															[styles.keyEventWithIcon]: keyEvent.eventType === "comment",
														})}
														key={keyEvent.timeFromRecordingStart}
														data-key-event-time={keyEvent.timeFromRecordingStart}
														style={{ left: `${position}%` }}
													>
														{keyEvent.eventType === "comment" && <FontAwesomeIcon icon={faMessageLines} />}
													</div>
												</Tooltip>
											);
										})}
									</div>
								)}
							</div>
						</div>
					</ControlTooltip>
				))}
			</div>
		</div>
	);
}
